import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { interviewsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';

export default function InterviewRoomPage() {
  const { accessCode } = useParams();
  const [interview, setInterview] = useState(null);
  const [session, setSession] = useState(null);
  const [currentQ, setCurrentQ] = useState(0);
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [candidateName, setCandidateName] = useState('');
  const [candidateEmail, setCandidateEmail] = useState('');
  const [showBioConsent, setShowBioConsent] = useState(false);
  const [bioConsentChecked, setBioConsentChecked] = useState(false);
  const [aiEvalConsentChecked, setAiEvalConsentChecked] = useState(false);
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [transcripts, setTranscripts] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMsg, setWarningMsg] = useState('');
  const [cameraActive, setCameraActive] = useState(false);
  const [faceDetected, setFaceDetected] = useState(true);
  const [antiCheatCounts, setAntiCheatCounts] = useState({ tab_switch:0, no_face:0 });

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);
  const sessionIdRef = useRef(null);

  const currentQRef = useRef(currentQ);
  currentQRef.current = currentQ;

  const logCheat = useCallback(async (type, details) => {
    if (!sessionIdRef.current) return;
    try {
      await interviewsAPI.logAntiCheat(sessionIdRef.current, { type, details, question_index: currentQRef.current });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Interview anti-cheat log failed:', err);
    }
  }, []);

  useEffect(() => { loadInterview(); return () => stopCamera(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessCode]);

  // Anti-cheat: tab switch
  useEffect(() => {
    if (!started || completed) return;
    const handleVis = () => {
      if (document.hidden) {
        logCheat('tab_switch', 'Left interview tab');
        setAntiCheatCounts(p => ({ ...p, tab_switch: p.tab_switch + 1 }));
        setWarningMsg('Tab switch detected during interview!');
        setShowWarning(true);
      }
    };
    document.addEventListener('visibilitychange', handleVis);
    return () => document.removeEventListener('visibilitychange', handleVis);
  }, [started, completed, logCheat]);

  const loadInterview = async () => {
    try {
      const res = await interviewsAPI.getByCode(accessCode);
      setInterview(res.data);
      setTranscripts(new Array(res.data.questions.length).fill(''));
    } catch (err) {
      toast.error('Interview not found');
    } finally { setLoading(false); }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setCameraActive(true);
    } catch (err) {
      toast.error('Camera access denied. Please allow camera and microphone.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  };

  const startSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.info('Speech recognition not supported. Please type your answer.');
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    let finalTranscript = '';
    recognition.onresult = (e) => {
      let interim = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalTranscript += e.results[i][0].transcript + ' ';
        else interim = e.results[i][0].transcript;
      }
      setTranscript(finalTranscript + interim);
    };
    recognition.onerror = (e) => {
      if (e.error !== 'no-speech' && process.env.NODE_ENV === 'development') console.error('Speech error:', e.error);
    };
    recognition.start();
    recognitionRef.current = recognition;
    setRecording(true);
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setRecording(false);
  };

  const handleStart = async () => {
    if (!candidateName.trim() || !candidateEmail.trim()) {
      toast.error('Please enter your name and email'); return;
    }
    setShowBioConsent(true);
  };

  const handleConfirmConsent = async () => {
    if (!bioConsentChecked || !aiEvalConsentChecked) {
      toast.error('Please provide both consents to continue'); return;
    }
    try {
      await startCamera();
      const nowIso = new Date().toISOString();
      const res = await interviewsAPI.startSession(interview._id, {
        candidate_name: candidateName,
        candidate_email: candidateEmail,
        access_code: accessCode,
        biometric_consent: true,
        biometric_consent_at: nowIso,
        biometric_consent_version: '1.0',
        ai_evaluation_consent: true,
        ai_evaluation_consent_at: nowIso,
        ai_evaluation_consent_version: '1.0',
      });
      setSession(res.data);
      // Candidate ops (transcript / anti-cheat / complete) authorize with the
      // session access token — never the MongoDB id.
      sessionIdRef.current = res.data.session_access_token;
      setShowBioConsent(false);
      setStarted(true);
      toast.success('Interview started. Answer each question verbally or by typing.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start');
    }
  };

  const handleDeclineConsent = () => {
    setShowBioConsent(false);
    setBioConsentChecked(false);
    setAiEvalConsentChecked(false);
    toast.info('You can take the interview only after providing the required consents. You may close this window.');
  };

  const saveCurrentTranscript = async () => {
    if (!sessionIdRef.current) return;
    const t = transcript || transcripts[currentQ] || '';
    const newT = [...transcripts]; newT[currentQ] = t; setTranscripts(newT);
    try {
      await interviewsAPI.saveTranscript(sessionIdRef.current, { question_index: currentQ, transcript: t });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Save transcript failed:', err);
    }
  };

  const nextQuestion = async () => {
    stopSpeechRecognition();
    await saveCurrentTranscript();
    if (currentQ < interview.questions.length - 1) {
      setCurrentQ(currentQ + 1);
      setTranscript(transcripts[currentQ + 1] || '');
    }
  };

  const handleComplete = async () => {
    stopSpeechRecognition();
    await saveCurrentTranscript();
    stopCamera();
    try {
      await interviewsAPI.completeSession(sessionIdRef.current);
      setCompleted(true);
      toast.success('Interview submitted for AI scoring!');
    } catch (err) { toast.error('Failed to submit'); }
  };

  if (loading) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F1EAE1',color:'hsl(var(--muted-foreground))' }}>Loading...</div>;
  if (!interview) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F1EAE1',color:'hsl(var(--muted-foreground))' }}>Interview not found</div>;

  if (completed) {
    return (
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#F1EAE1' }}>
        <Card style={{ background:'hsl(var(--card))',border:'1px solid hsl(var(--border))',borderRadius:14,padding:'3rem',textAlign:'center',maxWidth:480 }}>
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2" style={{ margin:'0 auto 1.5rem' }}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          <h2 style={{ fontFamily:'Cabinet Grotesk',fontSize:'1.5rem',fontWeight:700,marginBottom:'0.75rem' }}>Interview Complete!</h2>
          <p style={{ fontSize:'0.9375rem',color:'hsl(var(--muted-foreground))',lineHeight:1.6 }}>Your responses are being scored by AI. The recruiter will review shortly.</p>
          <p style={{ fontSize:'0.75rem',color:'hsl(var(--muted-foreground))',marginTop:'1.25rem' }}>
            Want your interview data removed? You can <a href="/interview-data-request" data-testid="interview-data-request-link" style={{ color:'#0076A3', textDecoration:'underline' }}>request deletion</a> at any time (completed within 30 days).
          </p>
        </Card>
      </div>
    );
  }

  if (!started) {
    return (
      <div style={{ display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',background:'#F1EAE1',padding:'2rem' }}>
        <Card style={{ background:'hsl(var(--card))',border:'1px solid hsl(var(--border))',borderRadius:14,padding:'2.5rem',maxWidth:520,width:'100%' }}>
          <div style={{ display:'flex',alignItems:'center',gap:'0.75rem',marginBottom:'1.25rem' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#0076A3" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
            <h2 style={{ fontFamily:'Cabinet Grotesk',fontSize:'1.5rem',fontWeight:700 }}>AI Interview</h2>
          </div>
          <p style={{ fontSize:'0.8125rem',color:'hsl(var(--muted-foreground))',marginBottom:'0.25rem' }}>For: {interview.job_title}</p>
          <p style={{ fontSize:'0.875rem',color:'hsl(var(--muted-foreground))',marginBottom:'1.5rem' }}>Type: {interview.template_name} &middot; {interview.questions.length} questions</p>

          <div style={{ padding:'0.875rem',borderRadius:10,background:'rgba(30,104,255,0.08)',border:'1px solid rgba(30,104,255,0.15)',fontSize:'0.75rem',color:'#0076A3',marginBottom:'1.5rem' }}>
            <strong>How it works:</strong> You'll be asked questions one at a time. Use your microphone to answer verbally, or type your response. Your camera will be active for face presence monitoring.
          </div>

          <div style={{ display:'flex',flexDirection:'column',gap:'0.875rem',marginBottom:'1.5rem' }}>
            <input placeholder="Your full name" value={candidateName} onChange={e => setCandidateName(e.target.value)} data-testid="interview-candidate-name"
              style={{ width:'100%',padding:'0.75rem',borderRadius:10,border:'1px solid #CBD5E1',background:'#FAF7F2',color:'#002A38',fontSize:'0.875rem',outline:'none',boxSizing:'border-box' }} />
            <input placeholder="Your email" type="email" value={candidateEmail} onChange={e => setCandidateEmail(e.target.value)} data-testid="interview-candidate-email"
              style={{ width:'100%',padding:'0.75rem',borderRadius:10,border:'1px solid #CBD5E1',background:'#FAF7F2',color:'#002A38',fontSize:'0.875rem',outline:'none',boxSizing:'border-box' }} />
          </div>

          <Button onClick={handleStart} data-testid="interview-start-button" style={{ width:'100%',background:'#0076A3',color:'#fff',borderRadius:10,padding:'0.875rem',fontWeight:600,fontSize:'1rem' }}>
            Start Interview
          </Button>
        </Card>

        <Dialog open={showBioConsent} onOpenChange={(o) => { if (!o) handleDeclineConsent(); }}>
          <DialogContent data-testid="biometric-consent-dialog" style={{ maxWidth: 560 }}>
            <DialogHeader>
              <DialogTitle style={{ fontFamily:'Cabinet Grotesk' }}>Consent for AI Video Interview</DialogTitle>
            </DialogHeader>
            <div style={{ fontSize:'0.8125rem', color:'#4A5568', lineHeight:1.7, maxHeight:'52vh', overflowY:'auto', paddingRight:'0.25rem' }}>
              <p style={{ fontWeight:700, color:'#0076A3', marginBottom:'0.25rem' }}>1. Biometric data (camera monitoring)</p>
              <p>To maintain interview integrity, CHIRE uses your device camera to capture and analyse <strong>facial imagery (a biometric identifier)</strong> for face-presence monitoring and anti-cheating during this interview.</p>
              <ul style={{ paddingLeft:'1.25rem', margin:'0.5rem 0' }}>
                <li><strong>What we collect:</strong> live video/facial imagery and identity signals while the interview is in progress.</li>
                <li><strong>Purpose:</strong> verify your presence, deter impersonation/cheating, and assess interview integrity.</li>
                <li><strong>Retention &amp; destruction:</strong> biometric data is retained only as long as necessary for the hiring decision and then permanently deleted per our Biometric Data Retention Policy and applicable law.</li>
                <li><strong>No AI training / no sale:</strong> we do not sell your biometric data and do not use it to train AI models.</li>
              </ul>

              <p style={{ fontWeight:700, color:'#0076A3', margin:'0.75rem 0 0.25rem' }}>2. Artificial intelligence evaluation</p>
              <p>This interview uses <strong>artificial intelligence to analyse your recorded responses</strong> and help evaluate your fitness for the position. Here is how it works and what it considers:</p>
              <ul style={{ paddingLeft:'1.25rem', margin:'0.5rem 0' }}>
                <li><strong>How it works:</strong> your spoken/typed answers are transcribed and an AI language model scores each answer against role-relevant criteria, producing a summary the recruiter reviews. AI assists the decision; it does not make the final hiring decision on its own.</li>
                <li><strong>General types of characteristics evaluated:</strong> relevance and completeness of your answers, communication clarity, role-specific technical/domain knowledge, problem-solving and reasoning, and overall job fit. It does <em>not</em> evaluate physical appearance, race, or other protected characteristics.</li>
                <li><strong>Sharing:</strong> your interview is shared only with people at the hiring organisation (and the technology providers) whose expertise is necessary to evaluate you.</li>
                <li><strong>Your rights:</strong> you may withdraw consent or request deletion of your interview data at any time, and we will delete it within 30 days. Declining means you cannot proceed with this AI video interview; please contact the recruiter for an alternative.</li>
              </ul>
              <p style={{ fontSize:'0.75rem', color:'#718096' }}>These consents are collected to comply with the Illinois Artificial Intelligence Video Interview Act (820 ILCS 42), the Illinois Biometric Information Privacy Act (BIPA), GDPR (Art. 9), India's DPDP Act 2023, and PDPL/PDPA. See our <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" style={{ color:'#0076A3' }}>Privacy Policy</a>. To delete your data later, visit <a href="/interview-data-request" target="_blank" rel="noopener noreferrer" style={{ color:'#0076A3' }}>Interview Data Request</a>.</p>

              <label style={{ display:'flex', alignItems:'flex-start', gap:'0.625rem', marginTop:'0.875rem', cursor:'pointer' }}>
                <input type="checkbox" data-testid="biometric-consent-checkbox" checked={bioConsentChecked} onChange={(e) => setBioConsentChecked(e.target.checked)} style={{ marginTop:3, width:16, height:16, flexShrink:0 }} />
                <span style={{ fontSize:'0.8125rem', color:'#0076A3' }}>I consent to the collection and processing of my <strong>biometric data</strong> for this interview.</span>
              </label>
              <label style={{ display:'flex', alignItems:'flex-start', gap:'0.625rem', marginTop:'0.625rem', cursor:'pointer' }}>
                <input type="checkbox" data-testid="ai-eval-consent-checkbox" checked={aiEvalConsentChecked} onChange={(e) => setAiEvalConsentChecked(e.target.checked)} style={{ marginTop:3, width:16, height:16, flexShrink:0 }} />
                <span style={{ fontSize:'0.8125rem', color:'#0076A3' }}>I have read the explanation above and consent to be <strong>evaluated by the artificial intelligence program</strong> used in this video interview.</span>
              </label>
            </div>
            <DialogFooter style={{ gap:'0.5rem' }}>
              <Button onClick={handleDeclineConsent} data-testid="biometric-consent-decline" variant="outline" style={{ borderRadius:10 }}>Decline</Button>
              <Button onClick={handleConfirmConsent} disabled={!bioConsentChecked || !aiEvalConsentChecked} data-testid="biometric-consent-accept" style={{ background: (bioConsentChecked && aiEvalConsentChecked) ? '#0076A3' : '#94A3B8', color:'#fff', borderRadius:10 }}>
                I Consent &amp; Continue
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Active interview
  const question = interview.questions[currentQ];
  const isLast = currentQ === interview.questions.length - 1;

  return (
    <div style={{ minHeight:'100vh',background:'#F1EAE1',display:'flex',flexDirection:'column' }}>
      {/* Top bar */}
      <div style={{ height:56,borderBottom:'1px solid hsl(var(--border))',display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0 1.5rem',background:'#fff',position:'sticky',top:0,zIndex:50 }}>
        <div style={{ display:'flex',alignItems:'center',gap:'1rem' }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#0076A3" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>
          <span style={{ fontFamily:'Cabinet Grotesk',fontSize:'0.875rem',fontWeight:600 }}>Q {currentQ+1} of {interview.questions.length}</span>
        </div>
        <div style={{ display:'flex',alignItems:'center',gap:'0.75rem' }}>
          <div data-testid="interview-anti-cheat" style={{ display:'flex',alignItems:'center',gap:'0.375rem',padding:'0.25rem 0.625rem',borderRadius:999,
            background:antiCheatCounts.tab_switch>0?'rgba(245,158,11,0.15)':'rgba(16,185,129,0.15)',
            border:`1px solid ${antiCheatCounts.tab_switch>0?'rgba(245,158,11,0.3)':'rgba(16,185,129,0.3)'}`,fontSize:'0.6875rem' }}>
            <span className={`status-dot ${antiCheatCounts.tab_switch>0?'status-processing':'status-completed'}`} style={{ width:6,height:6 }} />
            <span style={{ color:antiCheatCounts.tab_switch>0?'#D97706':'#059669' }}>
              {antiCheatCounts.tab_switch>0?`${antiCheatCounts.tab_switch} flags`:'Monitored'}
            </span>
          </div>
          <Button data-testid="interview-end-button" onClick={handleComplete} variant="outline" style={{ borderRadius:10,fontSize:'0.8125rem',borderColor:'rgba(239,68,68,0.3)',color:'#DC2626' }}>
            End Interview
          </Button>
        </div>
      </div>

      {/* Progress */}
      <div style={{ height:3,background:'hsl(var(--border))' }}>
        <div style={{ height:'100%',background:'#0076A3',width:`${((currentQ+1)/interview.questions.length)*100}%`,transition:'width 0.3s' }} />
      </div>

      {/* Main area: split layout */}
      <div style={{ flex:1,display:'grid',gridTemplateColumns:'320px 1fr',gap:0 }}>
        {/* Left: video + controls */}
        <div style={{ borderRight:'1px solid hsl(var(--border))',display:'flex',flexDirection:'column',padding:'1.25rem' }}>
          <div data-testid="interview-video" style={{ width:'100%',aspectRatio:'4/3',borderRadius:12,overflow:'hidden',background:'#000',marginBottom:'1rem',position:'relative' }}>
            <video ref={videoRef} autoPlay muted playsInline style={{ width:'100%',height:'100%',objectFit:'cover',transform:'scaleX(-1)' }} />
            {!cameraActive && <div style={{ position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',color:'hsl(var(--muted-foreground))',fontSize:'0.8125rem' }}>Camera off</div>}
            {cameraActive && (
              <div style={{ position:'absolute',bottom:8,right:8,padding:'0.25rem 0.5rem',borderRadius:999,background:'rgba(0,0,0,0.6)',fontSize:'0.625rem',color:faceDetected?'#059669':'#DC2626' }}>
                {faceDetected?'Face detected':'No face'}
              </div>
            )}
          </div>

          {/* Mic controls */}
          <div style={{ display:'flex',gap:'0.5rem',marginBottom:'1rem' }}>
            <Button data-testid="interview-mic-toggle" onClick={() => recording?stopSpeechRecognition():startSpeechRecognition()}
              style={{ flex:1,borderRadius:10,background:recording?'rgba(239,68,68,0.15)':'rgba(30,104,255,0.15)',
                border:`1px solid ${recording?'rgba(239,68,68,0.3)':'rgba(30,104,255,0.3)'}`,
                color:recording?'#DC2626':'#0076A3',fontSize:'0.8125rem' }}>
              {recording ? (
                <span style={{ display:'flex',alignItems:'center',gap:'0.375rem' }}>
                  <span className="status-dot" style={{ width:8,height:8,background:'#EF4444',animation:'pulse 1.5s infinite' }} />
                  Recording... Click to stop
                </span>
              ) : 'Start Speaking'}
            </Button>
          </div>

          {/* Question nav dots */}
          <div style={{ display:'flex',gap:'0.375rem',justifyContent:'center' }}>
            {interview.questions.map((_,i) => (
              <div key={`iv-nav-${i}`} style={{ width:10,height:10,borderRadius:'50%',background:i===currentQ?'#0076A3':transcripts[i]?'#10B981':'#CBD5E1' }} />
            ))}
          </div>
        </div>

        {/* Right: question + answer */}
        <div data-testid="interview-ai-agent-panel" style={{ display:'flex',flexDirection:'column',padding:'2rem' }}>
          <div style={{ marginBottom:'1.5rem' }}>
            <Badge style={{ background:'rgba(30,104,255,0.1)',color:'#0076A3',border:'1px solid rgba(30,104,255,0.2)',borderRadius:999,fontSize:'0.6875rem',marginBottom:'0.75rem' }}>
              {question.skill_tested}
            </Badge>
            <h2 style={{ fontFamily:'Cabinet Grotesk',fontSize:'1.375rem',fontWeight:600,lineHeight:1.5 }}>{question.question}</h2>
            {question.time_limit_seconds && (
              <p style={{ fontSize:'0.75rem',color:'hsl(var(--muted-foreground))',marginTop:'0.5rem' }}>Suggested time: {Math.ceil(question.time_limit_seconds/60)} min</p>
            )}
          </div>

          {/* Transcript area */}
          <div style={{ flex:1,marginBottom:'1.5rem' }}>
            <label style={{ display:'block',fontSize:'0.75rem',fontWeight:600,color:'hsl(var(--muted-foreground))',marginBottom:'0.375rem' }}>Your Response {recording && <span style={{ color:'#EF4444' }}>(listening...)</span>}</label>
            <textarea value={transcript} onChange={e => setTranscript(e.target.value)} placeholder="Speak into your microphone or type your answer here..."
              rows={8} style={{ width:'100%',padding:'1rem',borderRadius:10,border:'1px solid #CBD5E1',background:'#fff',color:'#002A38',fontSize:'0.875rem',outline:'none',resize:'vertical',boxSizing:'border-box',lineHeight:1.6 }} />
          </div>

          {/* Navigation */}
          <div style={{ display:'flex',justifyContent:'space-between' }}>
            <Button onClick={() => { stopSpeechRecognition(); saveCurrentTranscript(); if(currentQ>0){setCurrentQ(currentQ-1);setTranscript(transcripts[currentQ-1]||'');} }}
              disabled={currentQ===0} variant="outline" style={{ borderRadius:10 }}>Previous</Button>
            {isLast ? (
              <Button data-testid="interview-submit-button" onClick={handleComplete} style={{ background:'#10B981',color:'#F1EAE1',borderRadius:10,fontWeight:600 }}>Submit Interview</Button>
            ) : (
              <Button onClick={nextQuestion} style={{ background:'#0076A3',color:'#fff',borderRadius:10,fontWeight:600 }}>Next Question</Button>
            )}
          </div>
        </div>
      </div>

      {/* Warning */}
      <Dialog open={showWarning} onOpenChange={setShowWarning}>
        <DialogContent style={{ background:'#fff',border:'1px solid rgba(245,158,11,0.3)',borderRadius:14,maxWidth:400 }}>
          <DialogHeader><DialogTitle style={{ fontFamily:'Cabinet Grotesk',color:'#F59E0B' }}>Warning</DialogTitle></DialogHeader>
          <p style={{ fontSize:'0.875rem',color:'hsl(var(--muted-foreground))' }}>{warningMsg}</p>
          <DialogFooter><Button onClick={() => setShowWarning(false)} style={{ background:'#0076A3',color:'#fff',borderRadius:10,fontWeight:600 }}>I Understand</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
